/**
 * Accès d'urgence / héritage (`R17`, conception : `docs/EMERGENCY-ACCESS-DESIGN.md`).
 *
 * Le titulaire désigne un contact de confiance. Il lui remet HORS LIGNE un
 * KIT (secret de 256 bits, imprimé ou donné en main propre) et dépose sur le
 * serveur la `vaultKey` scellée sous ce kit :
 *
 * ```
 * kitSecret     = 256 bits du CSPRNG -> remis au contact, JAMAIS au serveur
 * sealingKey    = HKDF-SHA256(kitSecret, info="…|emergency-sealing-key")
 * accessProof   = HKDF-SHA256(kitSecret, info="…|emergency-access-proof")   -> présentée par le contact
 * verifier      = SHA-256("…|emergency-access-verifier" || accessProof)      -> stocké par le serveur
 * protectedKey  = seal(sealingKey, vaultKey, contexte="…|emergency-protected-symmetric-key", AAD=grantId)
 * ```
 *
 * C'est un 2-sur-2 PAR POSSESSION, sans aucune primitive nouvelle :
 *
 *  - le serveur seul a `protectedKey` mais pas le kit -> il ne déchiffre rien.
 *    Il n'existe AUCUN séquestre exploitable côté serveur (cf. ETH Zurich 2026 :
 *    le séquestre de clés des gestionnaires étudiés tombait avec le serveur) ;
 *  - le contact seul a le kit mais pas `protectedKey` : le serveur ne la lui
 *    remet qu'à l'expiration du délai d'attente, si le titulaire n'a pas refusé ;
 *  - aucune clé publique n'est récupérée auprès du serveur : l'authentification
 *    du contact, c'est la remise en main propre du kit. Un serveur malveillant
 *    ne peut donc pas substituer SA clé à celle du contact (attaque de
 *    substitution de clé publique décrite par ETH Zurich).
 *
 * ⚠️ Limite assumée, écrite dans la conception : le DÉLAI et le REFUS sont
 * appliqués par le serveur. Un serveur compromis ET complice du contact peut
 * les contourner — mais jamais ouvrir le coffre sans ce contact.
 */
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  EMERGENCY_KIT_SECRET_LENGTH,
  KDF_DOMAIN,
  SEAL_CONTEXT,
  SYMMETRIC_KEY_LENGTH,
  type AeadAlgorithmId,
} from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import {
  base32ToBytes,
  base64UrlToBytes,
  bytesToBase32,
  bytesToBase64Url,
  bytesToHex,
  concatBytes,
  utf8ToBytes,
  wipe,
} from './bytes.js';
import { InvalidParameterError } from './errors.js';
import { SecretKey, assertSecretKey } from './secret.js';

/** `vaultKey` scellée sous un kit d'accès d'urgence. Seule forme que le serveur voit. */
export type EmergencyProtectedSymmetricKey = SealedString;

/** Longueur de la preuve d'accès (octets, avant encodage base64url). */
const ACCESS_PROOF_LENGTH = 32;

/** Génère le secret d'un kit d'accès d'urgence (256 bits, CSPRNG). */
export function generateEmergencyKit(): SecretKey {
  return SecretKey.random(EMERGENCY_KIT_SECRET_LENGTH, 'emergencyKit');
}

/**
 * Formate le kit pour impression : base32 groupé par 4 (`A3F9-K2QW-…`), même
 * présentation que la clé de récupération. Purement cosmétique.
 */
export function formatEmergencyKit(kit: SecretKey): string {
  assertSecretKey(kit, 'kit', EMERGENCY_KIT_SECRET_LENGTH);
  const encoded = bytesToBase32(kit.reveal());
  return (encoded.match(/.{1,4}/gu) ?? [encoded]).join('-');
}

/**
 * Relit un kit saisi par le contact (tolérant aux espaces, tirets, casse).
 *
 * @throws {InvalidParameterError} base32 invalide ou longueur inattendue — une
 *   clé de récupération (128 bits) présentée par erreur est refusée ici.
 */
export function parseEmergencyKit(formatted: string): SecretKey {
  const bytes = base32ToBytes(formatted);
  if (bytes.length !== EMERGENCY_KIT_SECRET_LENGTH) {
    wipe(bytes);
    throw new InvalidParameterError(
      `emergencyKit: ${EMERGENCY_KIT_SECRET_LENGTH} octets attendus, ${bytes.length} recus`,
    );
  }
  return SecretKey.adopt(bytes, 'emergencyKit', EMERGENCY_KIT_SECRET_LENGTH);
}

/** `sealingKey = HKDF-SHA256(kitSecret, info="…|emergency-sealing-key")`. Ne quitte jamais l'appareil. */
export function deriveEmergencySealingKey(kit: SecretKey): SecretKey {
  assertSecretKey(kit, 'kit', EMERGENCY_KIT_SECRET_LENGTH);
  const bytes = hkdf(sha256, kit.reveal(), undefined, utf8ToBytes(KDF_DOMAIN.emergencySealingKey), SYMMETRIC_KEY_LENGTH);
  return SecretKey.adopt(bytes, 'emergencySealingKey', SYMMETRIC_KEY_LENGTH);
}

/**
 * Preuve d'accès (base64url sans remplissage) : ce que le CONTACT présente au
 * serveur pour déclencher puis retirer l'accès. Dérivée dans un domaine HKDF
 * distinct de la clé de scellement : la connaître n'ouvre rien.
 */
export function deriveEmergencyAccessProof(kit: SecretKey): string {
  assertSecretKey(kit, 'kit', EMERGENCY_KIT_SECRET_LENGTH);
  const bytes = hkdf(sha256, kit.reveal(), undefined, utf8ToBytes(KDF_DOMAIN.emergencyAccessProof), ACCESS_PROOF_LENGTH);
  try {
    return bytesToBase64Url(bytes).replace(/=+$/u, '');
  } finally {
    wipe(bytes);
  }
}

/**
 * Vérificateur stocké par le serveur (hex) : `SHA-256(domaine || preuve)`.
 * Le TITULAIRE l'envoie à la création (le serveur ne voit donc la preuve
 * qu'au moment où le contact s'en sert) ; le serveur le recalcule à partir de
 * la preuve présentée. Une fuite de la base ne donne pas la preuve.
 *
 * @throws {InvalidParameterError} preuve mal formée
 */
export function emergencyAccessVerifier(accessProof: string): string {
  if (typeof accessProof !== 'string' || accessProof.length === 0) {
    throw new InvalidParameterError('accessProof: chaine base64url attendue');
  }
  const padded = accessProof + '='.repeat((4 - (accessProof.length % 4)) % 4);
  const proof = base64UrlToBytes(padded);
  try {
    if (proof.length !== ACCESS_PROOF_LENGTH) {
      throw new InvalidParameterError(`accessProof: ${ACCESS_PROOF_LENGTH} octets attendus, ${proof.length} recus`);
    }
    return bytesToHex(sha256(concatBytes(utf8ToBytes(KDF_DOMAIN.emergencyAccessVerifier), proof)));
  } finally {
    wipe(proof);
  }
}

function grantAad(grantId: string): Uint8Array {
  if (typeof grantId !== 'string' || grantId.length === 0) {
    throw new InvalidParameterError('grantId: chaine non vide attendue');
  }
  return utf8ToBytes(grantId);
}

export interface SealEmergencyAccessInput {
  readonly vaultKey: SecretKey;
  readonly kit: SecretKey;
  /** Identifiant public de la délégation, lié en AAD : un serveur ne peut pas permuter deux délégations. */
  readonly grantId: string;
  readonly algorithm?: AeadAlgorithmId;
}

/** Scelle la `vaultKey` sous le kit (nonce frais). */
export function sealEmergencyAccess(input: SealEmergencyAccessInput): EmergencyProtectedSymmetricKey {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  const aad = grantAad(input.grantId);
  const sealingKey = deriveEmergencySealingKey(input.kit);
  try {
    return sealToString(sealingKey, input.vaultKey.reveal(), {
      context: SEAL_CONTEXT.emergencyProtectedSymmetricKey,
      associatedData: aad,
      algorithm: input.algorithm,
    });
  } finally {
    sealingKey.destroy();
  }
}

export interface OpenEmergencyAccessInput {
  readonly protectedKey: EmergencyProtectedSymmetricKey;
  readonly kit: SecretKey;
  readonly grantId: string;
}

/**
 * Ouvre la `vaultKey` d'un titulaire, côté CONTACT, une fois le délai écoulé.
 *
 * @throws {DecryptionFailedError} mauvais kit, blob altéré, ou blob d'une autre délégation/voie
 * @throws {MalformedCiphertextError} conteneur structurellement invalide
 */
export function openEmergencyAccess(input: OpenEmergencyAccessInput): SecretKey {
  assertSecretKey(input?.kit, 'kit', EMERGENCY_KIT_SECRET_LENGTH);
  if (typeof input.protectedKey !== 'string' || input.protectedKey.length === 0) {
    throw new InvalidParameterError('protectedKey: blob d’accès d’urgence manquant');
  }
  const aad = grantAad(input.grantId);
  const sealingKey = deriveEmergencySealingKey(input.kit);
  try {
    const bytes = openFromString(sealingKey, input.protectedKey, {
      context: SEAL_CONTEXT.emergencyProtectedSymmetricKey,
      associatedData: aad,
    });
    if (bytes.length !== SYMMETRIC_KEY_LENGTH) {
      wipe(bytes);
      throw new InvalidParameterError('vaultKey dechiffree de longueur inattendue');
    }
    return SecretKey.adopt(bytes, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  } finally {
    sealingKey.destroy();
  }
}
